import type { LeaveStatus } from '@lms/shared-types'

/**
 * Schedule-conflict detection used by the manager Approvals page
 * (`ManagerRequestsPage`).
 *
 * A "schedule conflict" is two requests awaiting a manager decision whose
 * date ranges overlap — i.e. approving both would put two teammates off at
 * the same time. Only `pending` / `needs_review` rows participate: escalated
 * and director-queue rows are awaiting someone else's call, so they don't
 * count toward a manager's overlap cluster.
 *
 * Keeping this in one place guarantees the Home preview and the Approvals
 * queue mark the exact same rows as conflicting — they previously drifted
 * (Home used the AI verdict, Approvals used date overlap).
 */

export interface ConflictCandidate {
  id: string
  startDate: string
  endDate: string
  status: LeaveStatus
}

const ACTIONABLE_FOR_CONFLICT: ReadonlySet<LeaveStatus> = new Set<LeaveStatus>([
  'pending',
  'escalated',
])

/**
 * Returns a map of leave id → number of other leaves it overlaps. A row is
 * "in conflict" when its entry is > 0. Ranges are inclusive on both ends
 * (a leave ending on the same day another starts does not overlap, since the
 * comparison uses `<=` against the opposite boundary).
 */
export function computeScheduleConflicts(
  leaves: ReadonlyArray<ConflictCandidate>,
): Map<string, number> {
  const map = new Map<string, number>()
  const inScope = leaves.filter((l) => ACTIONABLE_FOR_CONFLICT.has(l.status))
  for (const a of inScope) {
    for (const b of inScope) {
      if (a.id >= b.id) continue
      if (a.startDate <= b.endDate && b.startDate <= a.endDate) {
        map.set(a.id, (map.get(a.id) ?? 0) + 1)
        map.set(b.id, (map.get(b.id) ?? 0) + 1)
      }
    }
  }
  return map
}

/**
 * Returns a map of leave id → the other requests it overlaps (its "peers"),
 * using the same actionable filter + inclusive-overlap rule as
 * `computeScheduleConflicts`. Generic so callers get their own row type back
 * (e.g. `LeaveListItem`) with employee names for a "conflicts with…" surface.
 */
export function computeConflictPeers<T extends ConflictCandidate>(
  leaves: ReadonlyArray<T>,
): Map<string, T[]> {
  const map = new Map<string, T[]>()
  const inScope = leaves.filter((l) => ACTIONABLE_FOR_CONFLICT.has(l.status))
  const add = (id: string, peer: T): void => {
    const arr = map.get(id)
    if (arr) arr.push(peer)
    else map.set(id, [peer])
  }
  for (const a of inScope) {
    for (const b of inScope) {
      if (a.id >= b.id) continue
      if (a.startDate <= b.endDate && b.startDate <= a.endDate) {
        add(a.id, b)
        add(b.id, a)
      }
    }
  }
  return map
}

/** Convenience: the set of leave ids that overlap at least one peer. */
export function scheduleConflictIds(
  leaves: ReadonlyArray<ConflictCandidate>,
): Set<string> {
  return new Set(computeScheduleConflicts(leaves).keys())
}

/**
 * One transitive overlap group. A leaves B; B overlaps C (but not A);
 * → one group { ids: {A,B,C}, windowStart: min(start), windowEnd: max(end) }.
 *
 * The Approvals page renders one banner per group so non-adjacent clusters
 * (e.g. a March overlap vs a May overlap) don't get lumped under a single
 * misleading date header.
 */
export interface ConflictGroup<T extends ConflictCandidate = ConflictCandidate> {
  /** The leaves in this transitive overlap group, original order preserved. */
  leaves: ReadonlyArray<T>
  /** Earliest startDate across the group (ISO yyyy-mm-dd). */
  windowStart: string
  /** Latest endDate across the group (ISO yyyy-mm-dd). */
  windowEnd: string
}

/**
 * Groups leaves by transitive date overlap via union-find. Only
 * `pending` / `needs_review` rows are considered — same scope as
 * :func:`computeScheduleConflicts`. Singletons (no peer overlap) are
 * dropped: a group of one isn't a conflict.
 *
 * Input order is preserved both within each group (`leaves`) AND across
 * the returned list — clusters appear in the order of their first
 * member's input index. The Approvals page passes its pending rows in
 * first-come-first-serve order, so clusters come back ordered by their
 * oldest member's submission time. (This comment used to claim the server
 * sorted by `ai_priority_score desc`; that ordering was removed when FCFS
 * became a hard rule.)
 */
export function groupConflictClusters<T extends ConflictCandidate>(
  leaves: ReadonlyArray<T>,
): ReadonlyArray<ConflictGroup<T>> {
  const inScope = leaves.filter((l) => ACTIONABLE_FOR_CONFLICT.has(l.status))
  if (inScope.length < 2) return []

  // Union-find over inScope indices.
  const parent = inScope.map((_, i) => i)
  const find = (i: number): number => {
    let r = i
    while (parent[r] !== r) r = parent[r] as number
    let c = i
    while (parent[c] !== c) {
      const next = parent[c] as number
      parent[c] = r
      c = next
    }
    return r
  }
  const union = (a: number, b: number): void => {
    const ra = find(a)
    const rb = find(b)
    if (ra !== rb) parent[ra] = rb
  }

  for (let i = 0; i < inScope.length; i += 1) {
    for (let j = i + 1; j < inScope.length; j += 1) {
      const a = inScope[i] as T
      const b = inScope[j] as T
      if (a.startDate <= b.endDate && b.startDate <= a.endDate) {
        union(i, j)
      }
    }
  }

  const byRoot = new Map<number, { members: T[]; firstIndex: number }>()
  for (let i = 0; i < inScope.length; i += 1) {
    const root = find(i)
    const leaf = inScope[i] as T
    const bucket = byRoot.get(root)
    if (bucket) bucket.members.push(leaf)
    else byRoot.set(root, { members: [leaf], firstIndex: i })
  }

  const groups: Array<ConflictGroup<T> & { _firstIndex: number }> = []
  for (const { members, firstIndex } of byRoot.values()) {
    if (members.length < 2) continue
    const starts = members.map((l) => l.startDate).sort()
    const ends = members.map((l) => l.endDate).sort()
    groups.push({
      leaves: members,
      windowStart: starts[0] as string,
      windowEnd: ends[ends.length - 1] as string,
      _firstIndex: firstIndex,
    })
  }
  // Clusters ranked by their highest-ranked member's input index, so a
  // priority-sorted caller surfaces the most-urgent cluster first.
  groups.sort((a, b) => a._firstIndex - b._firstIndex)
  return groups.map(({ _firstIndex: _, ...g }) => g)
}
